/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import { ArrowUpRight, Target, ShieldCheck, Users, Leaf, Compass, Rocket, Search, FileText, HardHat, KeyRound } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, ImageReveal } from "@/components/Reveal";
import MarqueeBand from "@/components/MarqueeBand";
import { IMG } from "@/lib/images";

const VALUES = [
  { icon: Target, t: "Precisione tecnica", d: "Ogni applicazione segue schede tecniche e collaudi. Nessuna approssimazione in cantiere." },
  { icon: ShieldCheck, t: "Garanzia sui lavori", d: "Materiali certificati e garanzie fino a 10 anni sulle impermeabilizzazioni in poliurea." },
  { icon: Users, t: "Un unico interlocutore", d: "Dalla progettazione alla consegna: coordiniamo tutte le fasi del tuo cantiere." },
  { icon: Leaf, t: "Efficienza energetica", d: "Soluzioni che riducono i consumi e aumentano il valore dell'immobile." },
];

export default function ChiSiamo() {
  return (
    <div data-testid="chisiamo-page">
      <PageHeader
        index="/ 01"
        title="Chi Siamo"
        lines={["L'ingegneria", "del costruire", "bene."]}
        subtitle="Greenhouse Solutions SPA è un'impresa edile specializzata in ristrutturazioni residenziali chiavi in mano, con expertise verticale nell'impermeabilizzazione in poliurea e nella coibentazione in poliuretano."
        logo
      />

      <section className="pb-24 bg-background">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-stretch">
          <ImageReveal className="lg:col-span-5">
            <div className="relative w-full h-full min-h-[440px] overflow-hidden">
              <img src={IMG.team} alt="Team Greenhouse Solutions" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover object-[center_bottom]" />
            </div>
          </ImageReveal>
          <div className="lg:col-span-7 flex flex-col justify-center">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-widest text-primary mb-6">// La nostra storia</p>
              <h2 className="font-head font-black text-4xl md:text-5xl tracking-tightest leading-[0.95] mb-6">
                Materiali industriali,<br />applicati al residenziale.
              </h2>
              <div className="space-y-5 text-muted-foreground leading-relaxed">
                <p>
                  Greenhouse Solutions SPA nasce dall'incontro tra competenza edile e cultura
                  dell'innovazione tecnica. Abbiamo scelto una strada precisa: portare
                  nell'edilizia residenziale materiali e metodologie normalmente riservati al
                  mondo industriale, dove tolleranze, collaudi e prestazioni non ammettono
                  approssimazioni.
                </p>
                <p>
                  Operiamo in tutta la Puglia e sull'intero territorio nazionale, con
                  un'expertise verticale nell'impermeabilizzazione in poliurea e nella
                  coibentazione in poliuretano a spruzzo — sistemi capaci di garantire durabilità,
                  continuità e risultati impossibili da ottenere con i metodi tradizionali.
                  Attorno a queste specializzazioni abbiamo costruito un servizio completo di
                  ristrutturazione chiavi in mano, dalla riqualificazione energetica e sismica
                  fino alle finiture e al risanamento dell'umidità.
                </p>
                <p>
                  Ogni cantiere è per noi un banco di prova e una responsabilità: seguiamo il
                  cliente con un unico interlocutore, tempi certi e materiali certificati.
                  Crediamo che il lavoro fatto a regola d'arte sia il miglior modo per crescere —
                  un cantiere, e una fiducia conquistata, alla volta.
                </p>
              </div>
              <Link to="/lavora-con-noi" data-testid="chisiamo-cta" className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-widest px-6 py-4 hover:bg-foreground transition-colors">
                Lavora con noi <ArrowUpRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <MarqueeBand />

      {/* MISSION & VISION */}
      <section className="py-24 bg-secondary/40 border-y border-border">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">// Chi vogliamo essere</p>
            <h2 className="font-head font-black text-3xl md:text-5xl tracking-tightest mb-14 max-w-3xl leading-[0.95]">
              La direzione che diamo a ogni progetto.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
            <Reveal>
              <div className="bg-card p-8 md:p-12 h-full">
                <Rocket className="w-9 h-9 text-primary mb-6" />
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">La nostra missione</p>
                <h3 className="font-head font-bold text-2xl md:text-3xl tracking-tight mb-5">Costruire meglio, per durare nel tempo.</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Offrire all'edilizia residenziale soluzioni tecniche ad alte prestazioni,
                  trasferendo nel privato il rigore dell'industria. Vogliamo che ogni intervento —
                  dall'impermeabilizzazione all'efficientamento energetico — sia sinonimo di
                  affidabilità, trasparenza e valore concreto per chi vive e possiede l'immobile.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="bg-card p-8 md:p-12 h-full">
                <Compass className="w-9 h-9 text-primary mb-6" />
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">La nostra visione</p>
                <h3 className="font-head font-bold text-2xl md:text-3xl tracking-tight mb-5">Il riferimento tecnico del territorio.</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Diventare il partner di riferimento per la riqualificazione degli edifici in
                  Puglia e in Italia, unendo innovazione dei materiali, sostenibilità ed efficienza
                  energetica. Immaginiamo un patrimonio edilizio più sicuro, performante e duraturo,
                  costruito con la stessa cura con cui lo consegneremmo a casa nostra.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <Reveal>
            <h2 className="font-head font-black text-3xl md:text-5xl tracking-tightest mb-14">Perché sceglierci</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.08}>
                <div className="bg-card p-8 h-full">
                  <v.icon className="w-8 h-8 text-primary mb-6" />
                  <h3 className="font-head font-bold text-xl tracking-tight mb-3">{v.t}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COME LAVORIAMO — 4-step process timeline + CTA */}
      <section className="py-24 md:py-32 bg-foreground text-background" data-testid="how-we-work">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14 md:mb-20">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-primary mb-3">// Come lavoriamo</p>
                <h2 className="font-head font-black text-3xl md:text-5xl tracking-tightest max-w-2xl leading-[0.95]">
                  Un processo chiaro,<br />dal primo contatto alla consegna.
                </h2>
              </div>
              <p className="font-mono text-xs uppercase tracking-widest text-background/60 max-w-xs md:text-right">
                Quattro fasi trasparenti che accompagnano il committente in ogni istante del cantiere.
              </p>
            </div>
          </Reveal>

          <div className="relative">
            {/* Connector line (desktop only) */}
            <div aria-hidden className="hidden md:block absolute top-8 left-8 right-8 h-px bg-background/15" />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-6 relative">
              {[
                { icon: Search, code: "01", t: "Sopralluogo", d: "Un tecnico dedicato visita il sito, valuta lo stato dell'immobile e ascolta le esigenze reali del committente." },
                { icon: FileText, code: "02", t: "Preventivo", d: "Ricevi un'offerta trasparente, con lavorazioni dettagliate, tempistiche stimate e materiali specificati." },
                { icon: HardHat, code: "03", t: "Cantiere", d: "Squadre certificate portano avanti i lavori nel rispetto delle norme di sicurezza e degli standard tecnici." },
                { icon: KeyRound, code: "04", t: "Consegna + Garanzia", d: "Consegna dell'opera con documentazione tecnica completa e garanzie contrattuali sui trattamenti applicati." },
              ].map((s, i) => (
                <Reveal key={s.code} delay={i * 0.1}>
                  <div data-testid={`process-step-${i}`} className="relative">
                    <div className="w-16 h-16 grid place-items-center bg-primary text-primary-foreground mb-6 relative z-10">
                      <s.icon className="w-7 h-7" />
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-primary mb-2">/ Step {s.code}</p>
                    <h3 className="font-head font-black text-2xl md:text-[26px] tracking-tightest leading-tight mb-3">{s.t}</h3>
                    <p className="font-mono text-xs uppercase tracking-widest text-background/60 leading-relaxed">{s.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* CTA — pre-selection via /preventivo URL params handled by Preventivo page */}
          <Reveal>
            <div className="mt-16 md:mt-24 flex flex-col md:flex-row md:items-center md:justify-between gap-6 border-t border-background/15 pt-10 md:pt-14">
              <p className="max-w-2xl text-base md:text-lg text-background/80 leading-relaxed">
                Pronto a partire? Puoi richiedere <span className="text-primary">un sopralluogo</span> o
                <span className="text-primary"> un preventivo</span> per il servizio che ti interessa: lo selezioni direttamente nel modulo.
              </p>
              <Link
                to="/preventivo"
                data-testid="how-we-work-cta"
                className="group inline-flex items-center gap-3 bg-primary text-primary-foreground font-mono text-xs md:text-sm uppercase tracking-widest px-7 md:px-9 py-4 md:py-5 md:hover:bg-background md:hover:text-foreground transition-colors self-start"
              >
                <span>Richiedi preventivo</span>
                <span className="w-6 h-[1px] bg-current transition-all duration-300 md:group-hover:w-10" />
                <ArrowUpRight className="w-4 h-4 md:w-5 md:h-5 transition-transform duration-300 md:group-hover:-translate-y-0.5 md:group-hover:translate-x-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
